import { Button } from '@mantine/core';
import { useState } from 'react';

import { CollectionScopeSelector } from '@/components/collection-scope-selector.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { useCollectionAppointments } from '@/hooks/use-collection-appointments.ts';

import { Appointment, CollectionActionScope } from '@/models/appointment/Appointment.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';

import { getAppointmentsExcludingSeriesUpdateCancelled, isPastAppointment } from '@/utils/appointmentUtils.ts';
import { getCustomerId } from '@/utils/customer-utils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

interface Props {
    onConfirm: (scope: CollectionActionScope) => void;
    onCancel: () => void;
    isEditMode?: boolean;
    appointment?: Appointment;
    patientInstanceId?: string;
}

export const CollectionEditScopeModal = ({
    onConfirm,
    appointment,
    patientInstanceId,
    onCancel,
    isEditMode,
}: Props) => {
    const [selectedScope, setSelectedScope] = useState<CollectionActionScope>(CollectionActionScope.SINGLE);
    const setCollectionData = useBookingStore((state) => state.setCollectionConfiguration);
    const setCollectionPastAppointments = useBookingStore((state) => state.setCollectionPastAppointments);
    const setCollectionOriginalFutureSlots = useBookingStore((state) => state.setCollectionOriginalFutureSlots);

    const {
        appointments,
        collection,
        error: collectionError,
        isLoading: isCollectionLoading,
    } = useCollectionAppointments({
        collectionId: appointment?.collection_id ?? '',
        instanceId: patientInstanceId ?? '',
        customerId: getCustomerId() ?? '',
        enabled: selectedScope === CollectionActionScope.ALL_FUTURE,
    });

    const handleConfirm = (): void => {
        if (selectedScope === CollectionActionScope.ALL_FUTURE) {
            if (appointments && collection?.series_config) {
                const now = dayjs();
                const startDate = dayjs(collection.series_config.start_date);
                const mappedConfig = {
                    start_date:
                        startDate.isValid() && startDate.isAfter(now, 'day')
                            ? startDate.format('YYYY-MM-DD')
                            : now.format('YYYY-MM-DD'),
                    interval_type: collection.series_config.interval_type,
                    interval_frequency: collection.series_config.interval_frequency,
                    preferred_availability: collection.series_config.preferred_availability,
                    preferred_time: collection.series_config.preferred_time,
                    number_of_appointments: collection.series_config.number_of_appointments,
                };
                const validAppointments = getAppointmentsExcludingSeriesUpdateCancelled(appointments);
                const pastAppointments = validAppointments.filter((apt) => isPastAppointment(now, apt));
                const futureSlots = validAppointments
                    .filter((apt) => !isPastAppointment(now, apt))
                    .map((apt) => apt.start);
                setCollectionPastAppointments(pastAppointments);
                setCollectionOriginalFutureSlots(futureSlots);
                setCollectionData(mappedConfig);
                onConfirm(selectedScope);
            }
        } else {
            onConfirm(selectedScope);
        }
    };

    return (
        <div className="flex flex-col gap-3">
            <Paragraph className="text-sm text-slate-500">
                Dieser Termin ist Teil einer Serie. Was möchten Sie bearbeiten?
            </Paragraph>

            <CollectionScopeSelector
                selectedScope={selectedScope}
                onChange={setSelectedScope}
                isEditMode={isEditMode}
            />

            {collectionError && selectedScope === CollectionActionScope.ALL_FUTURE && (
                <Paragraph className="text-sm text-red-500">
                    Es gab ein Problem beim Laden der Termindaten. Bitte versuchen Sie es erneut.
                </Paragraph>
            )}

            <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={onCancel}>
                    Abbrechen
                </Button>

                <Button
                    onClick={handleConfirm}
                    loading={isCollectionLoading}
                    disabled={collectionError && selectedScope === CollectionActionScope.ALL_FUTURE}
                >
                    Weiter
                </Button>
            </div>
        </div>
    );
};
